/* ============================================================
   Dathent — UpgradeModal (.upm-*)
   Focused conversion modal that replaces the hard redirect to the full
   billing page. Surfaces the single sellable plan (Brand Pilot, or Growth
   once the NEW tariff grid is live — see `newGridActive` below). Both generic
   actions lead to Plans for an explicit plan choice. The plan-card text
   reuses the existing PLAN_DEFS/NEW_PLAN_DEFS + PLAN_TEXT_KEYS/
   NEW_PLAN_TEXT_KEYS under the `settings.*` i18n namespace, so it can
   never drift from the billing tab / Plans page.
   ============================================================ */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icons.jsx';
import { PLAN_DEFS, PLAN_TEXT_KEYS, NEW_PLAN_DEFS, NEW_PLAN_TEXT_KEYS, startCheckout } from '../lib/plans.js';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { track } from '../lib/funnel.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { api } from '../services/api/client.js';
import { formatPaymentIssueAmount, paymentIssueLinks } from '../lib/paymentIssue.js';

const PILOT = PLAN_DEFS.find((p) => p.id === 'pilot');
const PILOT_KEYS = PLAN_TEXT_KEYS.pilot;
// Show the first 5 feature rows of the Brand Pilot plan.
const PILOT_FEAT_COUNT = Math.min(5, (PILOT?.feats || []).length);

// New-grid equivalent (docs/VYTIAG-tarify-core-video-2026-07-06.md §2).
// GROWTH_NEW is deliberately a SEPARATE constant from any legacy 'growth'
// lookup — see lib/plans.js's collision note: the legacy PLAN_DEFS also has
// an unrelated 'growth' (soon-placeholder) entry, and `PLAN_DEFS.find` above
// would resolve to that one, not this one, if the id string were reused
// against the wrong array.
const GROWTH_NEW = NEW_PLAN_DEFS.find((p) => p.id === 'growth');
const GROWTH_NEW_KEYS = NEW_PLAN_TEXT_KEYS.growth;
const GROWTH_NEW_FEAT_COUNT = Math.min(5, (GROWTH_NEW?.feats || []).length);
const STARTER_NEW = NEW_PLAN_DEFS.find((p) => p.id === 'starter');
const STARTER_NEW_KEYS = NEW_PLAN_TEXT_KEYS.starter;
const STARTER_NEW_FEAT_COUNT = Math.min(5, (STARTER_NEW?.feats || []).length);

// TASK-331: backend planKey -> display name, resolving the new-grid/legacy
// collision the same way the `plan` selection just below does. Used only by
// the 'renew' variant to name the org's `previousPlan`.
function resolvePlanName(planKey, { getPlanDisplay, getNewPlanDisplay, newGridActive }) {
  if (!planKey) return null;
  const display = (newGridActive && NEW_PLAN_DEFS.some((p) => p.id === planKey))
    ? getNewPlanDisplay(planKey)
    : getPlanDisplay(planKey);
  return display;
}

export default function UpgradeModal({ open, feature = 'default', variant = 'upgrade', onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getPlanDisplay, getNewPlanDisplay, newGridActive } = usePlanCatalog();
  const dashboard = useDashboardData();
  const postingPlan = dashboard.entitlements?.plan === 'posting';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const ctaRef = useRef(null);
  const prevFocusRef = useRef(null);
  const isRenew = variant === 'renew';

  // Single switch point: everything below reads from `plan` instead of the
  // hardcoded PILOT/PILOT_KEYS, so the rest of the component (and its JSX)
  // is unchanged whichever grid is active. Pre-flip (newGridActive === false)
  // this resolves to the EXACT same def/keys/display/checkout key as before —
  // byte-identical render.
  const plan = newGridActive
    ? (postingPlan
      ? { def: STARTER_NEW, keys: STARTER_NEW_KEYS, featCount: STARTER_NEW_FEAT_COUNT, display: getNewPlanDisplay('starter') }
      : { def: GROWTH_NEW, keys: GROWTH_NEW_KEYS, featCount: GROWTH_NEW_FEAT_COUNT, display: getNewPlanDisplay('growth') })
    : { def: PILOT, keys: PILOT_KEYS, featCount: PILOT_FEAT_COUNT, display: getPlanDisplay('pilot') };

  // TASK-331 (variant='renew'): the org's previousPlan (organizations.billing_plan,
  // retained verbatim on lapse — see entitlement-repository's I6 invariant) is
  // resolved to a display name/price the same way `plan` above resolves the
  // sellable card, so "Renew Growth — €49/mo" never drifts from what Settings/
  // Plans show for that same planKey. Fail-soft: an old backend without
  // `previousPlan` (or a plan whose catalog row is gone/not self-service —
  // brief D7) leaves renewDisplay/renewPriceKnown false and the modal falls
  // back to the generic "Renew your plan" -> /app/plans (R4) path.
  const previousPlanKey = dashboard.entitlements?.previousPlan || null;
  const renewDisplay = isRenew ? resolvePlanName(previousPlanKey, { getPlanDisplay, getNewPlanDisplay, newGridActive }) : null;
  const renewPlanName = renewDisplay?.name || null;
  const renewPriceKnown = !!(renewDisplay?.priceFormatted && renewDisplay?.isSelfService !== false);

  // TASK-331 (M3 fix): a lapsed org whose Stripe subscription is still LIVE
  // (past_due/unpaid, not yet canceled) has an open invoice — the gateway's
  // widened readPaymentIssue() exposes it here as entitlements.paymentIssue.
  // For that org, startCheckout(previousPlanKey) below (R3/R4, checkout
  // WITHOUT a trial) is the WRONG primary action: create-checkout's
  // maybeConvertExistingSubscription finds the live subscription and returns
  // alreadySubscribed, which used to silently bounce to Settings with no
  // explanation (the dead end the review flagged). The correct fast path for
  // a still-live subscription is settling the open invoice (R1's hosted-
  // invoice fallback; the true one-click charge is phase 3) — reuse the exact
  // link PaymentIssueNotice already renders, or the billing portal if Stripe
  // didn't attach a hosted invoice URL. Only a genuinely canceled subscription
  // (hasOpenInvoice false) should ever reach the checkout-without-trial path.
  const paymentIssue = dashboard.entitlements?.paymentIssue || null;
  const hasOpenInvoice = isRenew && !!paymentIssue?.hasOpenInvoice;
  const invoiceLinks = isRenew ? paymentIssueLinks(paymentIssue, typeof window !== 'undefined' ? window.location.href : null) : null;
  const invoiceAmount = isRenew ? formatPaymentIssueAmount(paymentIssue, i18n.language) : null;

  useEffect(() => {
    if (open && !dashboard.entitlements) dashboard.ensureDashboardData?.({ keys: ['entitlements'] });
  }, [open, dashboard.entitlements, dashboard.ensureDashboardData]);

  // Focus the primary button on open; restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = document.activeElement;
    setError(null);
    setBusy(false);
    const raf = requestAnimationFrame(() => ctaRef.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isResearch = feature === 'research';
  // Hero copy follows the grid (docs/tariff-selling-copy.md §2): the legacy
  // titleDefault/subDefault say "Upgrade to Brand Pilot", which would read as
  // a visible mismatch above a Growth card once newGridActive — the V2 keys
  // sell the Growth promise instead. Legacy path (pre-flip) byte-identical.
  // TASK-331: the 'renew' variant never reaches this ternary chain for its
  // own title/subtitle (see the isRenew branch below) — computed here anyway
  // so hook-free variable declarations stay unconditional and simple.
  const title = postingPlan
    ? t('postingWorkspace.lockTitle')
    : isResearch
    ? t('upgradeModal.titleResearch')
    : t(newGridActive ? 'upgradeModal.titleDefaultV2' : 'upgradeModal.titleDefault');
  const subtitle = postingPlan
    ? t('postingWorkspace.lockBody')
    : isResearch
    ? t('upgradeModal.subResearch')
    : t(newGridActive ? 'upgradeModal.subDefaultV2' : 'upgradeModal.subDefault');

  const { def: PLAN, keys: PLAN_KEYS, featCount: PLAN_FEAT_COUNT, display } = plan;

  const tierText = PLAN_KEYS.tier ? t(`settings.${PLAN_KEYS.tier}`) : PLAN.tier;
  const nameText = PLAN_KEYS.name ? t(`settings.${PLAN_KEYS.name}`) : PLAN.name;
  // DB-backed display catalog (docs/TARIFF-REWORK-BRIEF.md §3/§4): falls back
  // to PLAN_DEFS/i18n whenever the server field is null/missing or the fetch
  // hasn't resolved — byte-identical to the pre-Phase-4 hardcoded render.
  // Monthly price. Legacy Pilot still carries its real founding discount
  // (matches Stripe: €150/mo first 3 months, then €300/mo — PLAN.priceBadge/
  // priceThen/priceTag below). The new-grid Growth card has NO founding
  // discount (owner call, 2026-07-07): NEW_PLAN_DEFS.growth and
  // NEW_PLAN_TEXT_KEYS.growth carry no priceBadge/priceThen/priceTag fields at
  // all, so PLAN_KEYS.priceBadge/priceThen/priceTag and PLAN.priceBadge/
  // priceThen/priceTag are all undefined for Growth and every price* variable
  // below resolves to a falsy value (or display.promoBadge/promoThen, which
  // are also null since the DB catalog carries no promo fields for 'growth')
  // — nothing renders below the plain "€49/mo" line. Numbers come from the
  // resolver (DB else PLAN_DEFS/NEW_PLAN_DEFS); only the unit word is
  // translated (DB doesn't carry per-locale unit text).
  const priceUnit = PLAN_KEYS.priceUnit ? t(`settings.${PLAN_KEYS.priceUnit}`) : PLAN.priceUnit;
  const priceBadge = display.fromDb ? display.promoBadge : (PLAN_KEYS.priceBadge ? t(`settings.${PLAN_KEYS.priceBadge}`) : PLAN.priceBadge);
  // planPilotPriceThen interpolates {{price}} — the "was"/full price the
  // legacy Pilot founding discount reverts to (language-neutral number,
  // translated words). Growth has no priceThen key, so this is a no-op there.
  const priceThen = display.fromDb
    ? display.promoThen
    : (PLAN_KEYS.priceThen
      ? t(`settings.${PLAN_KEYS.priceThen}`, { price: display.priceWasFormatted || PLAN.priceWas })
      : PLAN.priceThen);
  const priceTag = PLAN_KEYS.priceTag ? t(`settings.${PLAN_KEYS.priceTag}`) : PLAN.priceTag;

  // TASK-331 (M3 fix): startCheckout now throws a distinguishable
  // ALREADY_SUBSCRIBED error instead of silently redirecting (lib/plans.js).
  // Prefer the localized copy for that one code; any other error's own
  // message (or the generic checkoutError fallback) is unaffected.
  const describeCheckoutError = (err) => (
    err?.code === 'ALREADY_SUBSCRIBED' ? t('upgradeModal.alreadySubscribed') : (err?.message || t('upgradeModal.checkoutError'))
  );

  const handleSeeAllPlans = () => {
    onClose?.();
    navigate('/app/plans');
  };

  // TASK-331 (R3/R4): renew via Stripe Checkout WITHOUT a trial. Reusing
  // startCheckout(planKey) with no `trialSignup` is deliberate — it is the
  // exact call shape Plans.jsx already uses for an existing customer
  // (handleSubscribeNew/handleSwitchPlan's checkout branch), which takes
  // create-checkout.ts's existing non-trial path (createTrialCheckoutFromConfig
  // only fires when trialSignup is truthy). This is what makes A15 hold: no
  // fresh trial_period_days for a returning customer. When the previous
  // plan's price can't be resolved (retired/repriced — brief D7), fall back to
  // R4's "choose a different plan" destination instead of guessing a price.
  const handleRenewCheckout = async () => {
    if (busy) return;
    setError(null);
    if (!(renewPriceKnown && previousPlanKey)) {
      onClose?.();
      navigate('/app/plans');
      return;
    }
    setBusy(true);
    try { track('renew_checkout_clicked', { feature, previousPlan: previousPlanKey }); } catch { /* fail-silent */ }
    try {
      await startCheckout(previousPlanKey);
    } catch (err) {
      setError(describeCheckoutError(err));
      setBusy(false);
    }
  };

  // TASK-331 (R5): change payment method via the Stripe billing portal —
  // same call PaymentIssueNotice's "Update card" already makes.
  const handleUpdateCard = async () => {
    if (portalBusy) return;
    setError(null);
    setPortalBusy(true);
    try { track('renew_update_card_clicked', { feature }); } catch { /* fail-silent */ }
    try {
      const result = await api.billing.createPortalSession({
          returnUrl: typeof window !== 'undefined' ? new URL('/app/settings?tab=billing', window.location.href).toString() : null,
          flow: 'payment_method_update',
      });
        const portalUrl = result?.checkoutUrl || result?.data?.checkoutUrl || result?.portalUrl || result?.data?.portalUrl;
      if (!portalUrl) throw new Error('missing_portal_url');
      window.location.assign(portalUrl);
    } catch (_) {
      setError(t('paymentIssue.portalError'));
      setPortalBusy(false);
    }
  };

  // TASK-331 (M3 fix): the renew variant's PRIMARY action when the org's
  // subscription is still live with an open invoice — settle it, don't start
  // a fresh checkout (see the `hasOpenInvoice` comment above `paymentIssue`).
  // Reuses PaymentIssueNotice's exact "open the hosted invoice in a new tab"
  // action; falls back to the billing portal when Stripe hasn't attached a
  // hosted invoice URL (e.g. a decline that never generated one).
  const handlePayOpenInvoice = () => {
    try { track('renew_pay_invoice_clicked', { feature }); } catch { /* fail-silent */ }
    if (invoiceLinks?.payNow) {
      window.open(invoiceLinks.payNow, '_blank', 'noopener,noreferrer');
    } else {
      handleUpdateCard();
    }
  };

  // TASK-331: the 'renew' face of this ONE modal (owner directive: do not
  // fork UpgradeModal into a second component). R1 (one-click pay-open-invoice)
  // is phase 3 — only R2 (portal) and R3/R4 (checkout without trial) ship here.
  if (isRenew) {
    const renewTitle = renewPlanName ? t('upgradeModal.renewTitle') : t('lapsed.modalTitle');
    const renewSubtitle = renewPlanName ? t('upgradeModal.renewBody', { plan: renewPlanName }) : t('lapsed.modalBody');
    const primaryLabel = renewPriceKnown
      ? t('lapsed.renewPlanPriced', { plan: renewPlanName, price: `${renewDisplay.priceFormatted}${renewDisplay.unit || ''}` })
      : renewPlanName
      ? t('lapsed.renewPlan', { plan: renewPlanName })
      : t('lapsed.renew');

    return (
      <div
        className="upm-scrim"
        role="presentation"
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div
          className="upm-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upm-title"
          aria-describedby="upm-sub"
        >
          <button type="button" className="upm-close" onClick={() => onClose?.()} aria-label={t('upgradeModal.close', 'Close')}>
            <Icon name="x" size={18} />
          </button>

          <div className="upm-head">
            <span className="upm-badge" aria-hidden="true"><Icon name="lock" size={22} /></span>
            <div className="upm-htext">
              <h2 id="upm-title" className="upm-title">{renewTitle}</h2>
              <p id="upm-sub" className="upm-sub">{renewSubtitle}</p>
            </div>
          </div>

          <p className="upm-sub">{t('lapsed.readOnlyHint')}</p>

          {error && (
            <div className="upm-error" role="alert">
              <Icon name="alert" size={14} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {hasOpenInvoice ? (
            // TASK-331 (M3 fix): subscription still live with an open invoice
            // (past_due/unpaid) — settle it, never startCheckout(previousPlan)
            // (that hits alreadySubscribed and used to dead-end silently).
            <button type="button" className="upm-allplans" onClick={handlePayOpenInvoice}>
              <Icon name="key" size={14} aria-hidden="true" />
              {invoiceAmount ? t('lapsed.payOpenInvoice', { amount: invoiceAmount }) : t('lapsed.payOpenInvoiceGeneric')}
            </button>
          ) : (
            <button type="button" className="upm-allplans" onClick={handleRenewCheckout} disabled={busy}>
              {busy ? <span className="upm-spinner" aria-hidden="true" /> : <Icon name="key" size={14} aria-hidden="true" />}
              {busy ? t('lapsed.renewing') : primaryLabel}
            </button>
          )}

          <button type="button" className="upm-cta" onClick={handleUpdateCard} disabled={portalBusy} ref={ctaRef}>
            <Icon name="arrowRight" size={16} aria-hidden="true" />
            {portalBusy ? t('paymentIssue.openingPortal') : t('lapsed.updateCard')}
          </button>

          <button
            type="button"
            className="upm-billing-link"
            onClick={handleSeeAllPlans}
            style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {t('lapsed.chooseAnotherPlan')}
          </button>

          <div className="upm-trust">
            <span className="upm-trust-item"><Icon name="lock" size={12} aria-hidden="true" /> {t('upgradeModal.secureStripe')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="upm-scrim"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="upm-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upm-title"
        aria-describedby="upm-sub"
      >
        <button type="button" className="upm-close" onClick={() => onClose?.()} aria-label={t('upgradeModal.close', 'Close')}>
          <Icon name="x" size={18} />
        </button>

        <div className="upm-head">
          <span className="upm-badge" aria-hidden="true"><Icon name="lock" size={22} /></span>
          <div className="upm-htext">
            <h2 id="upm-title" className="upm-title">{title}</h2>
            <p id="upm-sub" className="upm-sub">{subtitle}</p>
          </div>
        </div>

        <div className="upm-plan">
          <div className="upm-tier">{tierText}</div>
          <div className="upm-name">{nameText}</div>
          <div className="upm-price">
            <span className="upm-now">{display.priceFormatted || PLAN.price}</span>
            <span className="upm-unit">{priceUnit}</span>
            {(display.priceWasFormatted || PLAN.priceWas) && (
              <span className="upm-was">{display.priceWasFormatted || PLAN.priceWas}</span>
            )}
            {priceBadge && <span className="upm-disc">{priceBadge}</span>}
          </div>
          {priceThen && (
            <div className="upm-then">{priceThen}{priceTag ? <> · <b>{priceTag}</b></> : null}</div>
          )}
          <ul className="upm-feats">
            <li>
              <span className="upm-ck" aria-hidden="true"><Icon name="check" size={11} stroke={2.4} /></span>
              {t('postingPlan.higherTierContrast')}
            </li>
            {PLAN.feats.slice(0, PLAN_FEAT_COUNT).map((f, i) => (
              <li key={i}>
                <span className="upm-ck" aria-hidden="true"><Icon name="check" size={11} stroke={2.4} /></span>
                {PLAN_KEYS.feats?.[i] ? t(`settings.${PLAN_KEYS.feats[i]}`) : f}
              </li>
            ))}
          </ul>
        </div>

        <button type="button" className="upm-cta" onClick={handleSeeAllPlans} ref={ctaRef}>
          <Icon name="arrowRight" size={16} aria-hidden="true" />
          {t('upgradeModal.seeAllPlans')}
        </button>

        {error && (
          <div className="upm-error" role="alert">
            <Icon name="alert" size={14} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="upm-trust">
          <span className="upm-trust-item"><Icon name="lock" size={12} aria-hidden="true" /> {t('upgradeModal.secureStripe')}</span>
          <span className="upm-trust-sep" aria-hidden="true">·</span>
          <span className="upm-trust-item">{t('upgradeModal.cancelAnytime')}</span>
        </div>

        <button type="button" className="upm-allplans" onClick={handleSeeAllPlans}>
          <Icon name="key" size={14} aria-hidden="true" />
          {t('upgradeModal.reviewPlansCta')}
        </button>
      </div>
    </div>
  );
}
